import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Snackbar
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import NotificationSubscriber from '../../../components/notifications/NotificationSubscriber';
import { 
  getNotificationPreferences, 
  updateNotificationPreferences, 
  subscribeToPushNotifications,
  NotificationTypePreference,
  NotificationActionPreference
} from '../../../services/notificationService';
import settingsService from '../../../services/settingsService';
import settingsApi from '../../../services/settingsApi';
import { NotificationSettings, SmtpSettings } from '../../../types/settings';

const NotificationSettingsTab: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationTypePreference[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [pushEnabled, setPushEnabled] = useState<boolean>(false);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    enablePushNotifications: false,
    enableEmailNotifications: false,
    enableSmsNotifications: false,
    notifyOnBackupComplete: false,
    notifyOnBackupError: false,
    notifyOnExchangeRateUpdate: false,
    notifyOnSystemErrors: false,
    notifyOnNewCustomers: false,
    notifyOnLowStock: false,
    notifyOnPaymentReceived: false,
    emailRecipients: [],
    smsRecipients: []
  });

  // SMTP Ayarları state'leri
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings>({
    Server: '',
    Port: 587,
    Username: '',
    Password: '',
    FromEmail: '',
    FromName: '',
    EnableSsl: true,
    UseDefaultCredentials: false,
    Timeout: 20000
  });
  const [smtpLoading, setSmtpLoading] = useState<boolean>(false);
  const [smtpSaving, setSmtpSaving] = useState<boolean>(false);
  const [smtpTesting, setSmtpTesting] = useState<boolean>(false);
  const [smtpMessage, setSmtpMessage] = useState<string | null>(null);
  const [smtpError, setSmtpError] = useState<string | null>(null);

  useEffect(() => {
    loadPreferences();
    loadNotificationSettings();
    loadSmtpSettings();
    
    // Check if push notifications are already enabled
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(subscription => {
          setPushEnabled(Boolean(subscription));
        });
      });
    }
  }, []);
  
  // SMTP ayarlarını yükle
  const loadSmtpSettings = async () => {
    try {
      setSmtpLoading(true);
      setSmtpError(null);
      const res = await settingsApi.getSmtpSettings();
      if (res) {
        setSmtpSettings(prev => ({ ...prev, ...res }));
      }
    } catch (err: any) {
      setSmtpError(err?.message || 'SMTP ayarları yüklenirken bir hata oluştu');
    } finally {
      setSmtpLoading(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      setSettingsLoading(true);
      // Varsayılan değerler
      const defaultSettings: NotificationSettings = {
        enablePushNotifications: false,
        enableEmailNotifications: false,
        enableSmsNotifications: false,
        notifyOnBackupComplete: false,
        notifyOnBackupError: false,
        notifyOnExchangeRateUpdate: false,
        notifyOnSystemErrors: false,
        notifyOnNewCustomers: false,
        notifyOnLowStock: false,
        notifyOnPaymentReceived: false,
        emailRecipients: [],
        smsRecipients: []
      };

      try {
        // notificationService'den bildirim tercihlerini al ve genel ayarlara dönüştür
        const preferences = await getNotificationPreferences();
        
        // Tercihleri genel ayarlara dönüştür
        const settings: NotificationSettings = {
          enablePushNotifications: preferences.some(pref => 
            pref.actions.some(action => action.actionType === 'Push' && action.isEnabled)
          ),
          enableEmailNotifications: preferences.some(pref => 
            pref.actions.some(action => action.actionType === 'Email' && action.isEnabled)
          ),
          enableSmsNotifications: preferences.some(pref => 
            pref.actions.some(action => action.actionType === 'SMS' && action.isEnabled)
          ),
          notifyOnBackupComplete: preferences.some(pref => 
            pref.notificationType === 'BackupComplete' && 
            pref.actions.some(action => action.isEnabled)
          ),
          notifyOnBackupError: preferences.some(pref => 
            pref.notificationType === 'BackupError' && 
            pref.actions.some(action => action.isEnabled)
          ),
          notifyOnExchangeRateUpdate: preferences.some(pref => 
            pref.notificationType === 'ExchangeRateUpdate' && 
            pref.actions.some(action => action.isEnabled)
          ),
          notifyOnSystemErrors: preferences.some(pref => 
            pref.notificationType === 'SystemError' && 
            pref.actions.some(action => action.isEnabled)
          ),
          notifyOnNewCustomers: preferences.some(pref => 
            pref.notificationType === 'NewCustomer' && 
            pref.actions.some(action => action.isEnabled)
          ),
          notifyOnLowStock: preferences.some(pref => 
            pref.notificationType === 'LowStock' && 
            pref.actions.some(action => action.isEnabled)
          ),
          notifyOnPaymentReceived: preferences.some(pref => 
            pref.notificationType === 'PaymentReceived' && 
            pref.actions.some(action => action.isEnabled)
          ),
          emailRecipients: [],
          smsRecipients: []
        };
        
        setNotificationSettings(settings);
        // Senkronize et: Push bildirimleri durumunu güncelle
        if (settings.enablePushNotifications !== pushEnabled) {
          setPushEnabled(settings.enablePushNotifications);
        }
      } catch (settingsError) {
        console.warn('Bildirim ayarları yüklenirken hata (varsayılan değerler kullanılıyor):', settingsError);
        // Hata durumunda varsayılan değerleri kullan
        setNotificationSettings(defaultSettings);
      }
    } catch (err) {
      console.error('Bildirim ayarları yüklenirken hata:', err);
      setError('Bildirim ayarları yüklenirken bir hata oluştu.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // SMTP alan değişiklikleri
  const handleSmtpChange = (field: keyof SmtpSettings, value: any) => {
    setSmtpSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // SMTP ayarlarını kaydet
  const handleSaveSmtp = async () => {
    try {
      setSmtpSaving(true);
      setSmtpError(null);
      setSmtpMessage(null);
      const { ok, message, status } = await settingsApi.saveSmtpSettings(smtpSettings);
      if (ok) {
        setSmtpMessage(message || 'SMTP ayarları kaydedildi');
        // Sunucudaki güncel değeri UI'a yansıt
        await loadSmtpSettings();
      } else {
        if (status === 403) {
          setSmtpError('SMTP ayarlarını kaydetmek için Admin rolü gereklidir.');
        } else {
          setSmtpError(message || 'SMTP ayarları kaydedilemedi');
        }
      }
    } catch (err: any) {
      setSmtpError(err?.message || 'SMTP ayarları kaydedilirken bir hata oluştu');
    } finally {
      setSmtpSaving(false);
    }
  };

  // SMTP test gönderimi
  const handleTestSmtp = async () => {
    try {
      setSmtpTesting(true);
      setSmtpError(null);
      setSmtpMessage(null);
      const { ok, message } = await settingsApi.testSmtpSettings(smtpSettings);
      if (ok) {
        setSmtpMessage(message || 'Test başarılı');
      } else {
        setSmtpError(message || 'Test başarısız');
      }
    } catch (err: any) {
      setSmtpError(err?.message || 'SMTP test sırasında bir hata oluştu');
    } finally {
      setSmtpTesting(false);
    }
  };

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const data = await getNotificationPreferences();
      setPreferences(data);
      setError(null);
    } catch (err) {
      setError('Bildirim tercihleri yüklenirken bir hata oluştu.');
      console.error('Bildirim tercihleri yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePreference = (notificationType: string, actionType: string) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.notificationType === notificationType 
          ? {
              ...pref,
              actions: pref.actions.map(action =>
                action.actionType === actionType
                  ? { ...action, isEnabled: !action.isEnabled }
                  : action
              )
            }
          : pref
      )
    );
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      
      try {
        // Bildirim tercihlerini kaydet
        const preferencesSuccess = await updateNotificationPreferences(preferences);
        console.log('Bildirim tercihleri kaydedildi:', preferencesSuccess);
        
        if (preferencesSuccess) {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
          // Ayarları yeniden yükle
          await loadNotificationSettings();
        } else {
          setError('Bildirim ayarları kaydedilemedi. Lütfen daha sonra tekrar deneyin.');
          setTimeout(() => setError(null), 3000);
        }
      } catch (prefError) {
        console.error('Bildirim tercihleri kaydedilirken hata:', prefError);
        setError('Bildirim tercihleri kaydedilirken bir hata oluştu.');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      setError('Bildirim tercihleri kaydedilirken bir hata oluştu.');
      console.error('Bildirim tercihleri kaydedilirken hata:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePushSubscriptionChange = (isSubscribed: boolean) => {
    setPushEnabled(isSubscribed);
    // Yeni ayarları da güncelle
    setNotificationSettings(prev => ({
      ...prev,
      enablePushNotifications: isSubscribed
    }));
  };
  
  const handleNotificationSettingChange = (field: keyof NotificationSettings, value: any) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Genel ayar değişikliklerini preferences'a da yansıt
    if (field === 'enablePushNotifications' || field === 'enableEmailNotifications' || field === 'enableSmsNotifications') {
      const actionType = field === 'enablePushNotifications' ? 'Push' : 
                        field === 'enableEmailNotifications' ? 'Email' : 'SMS';
      
      setPreferences(prev => 
        prev.map(pref => ({
          ...pref,
          actions: pref.actions.map(action =>
            action.actionType === actionType
              ? { ...action, isEnabled: value }
              : action
          )
        }))
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <NotificationsActiveIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Anlık Bildirimler
        </Typography>
        
        <Typography variant="body1" paragraph>
          Tarayıcı bildirimlerini etkinleştirerek, uygulama açık olmasa bile önemli olaylar hakkında bilgilendirilirsiniz.
        </Typography>
        
        <NotificationSubscriber onSubscriptionChange={handlePushSubscriptionChange} />
        
        <Alert severity="info" sx={{ mt: 2 }}>
          {pushEnabled 
            ? 'Tarayıcı bildirimleri etkinleştirildi. Artık önemli olaylar hakkında bildirim alacaksınız.' 
            : 'Tarayıcı bildirimlerini etkinleştirerek, uygulama açık olmasa bile önemli güncellemeler alabilirsiniz.'}
        </Alert>
      </Paper>
      
      {/* Genel Bildirim Ayarları */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Genel Bildirim Ayarları
        </Typography>
        
        {settingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} display="grid">
            <Grid sx={{ gridColumn: 'span 12' }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={notificationSettings.enablePushNotifications || false} 
                    onChange={(e) => handleNotificationSettingChange('enablePushNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Tarayıcı Bildirimleri"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: 'span 12' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.enableEmailNotifications || false}
                    onChange={(e) => handleNotificationSettingChange('enableEmailNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="E-posta Bildirimleri"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: 'span 12' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.enableSmsNotifications || false}
                    onChange={(e) => handleNotificationSettingChange('enableSmsNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="SMS Bildirimleri"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: 'span 12' }}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Bildirim Alınacak Olaylar</Typography>
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnBackupComplete || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnBackupComplete', e.target.checked)}
                    color="primary"
                  />
                }
                label="Yedekleme Tamamlandığında"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnBackupError || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnBackupError', e.target.checked)}
                    color="primary"
                  />
                }
                label="Yedekleme Hatalarında"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnExchangeRateUpdate || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnExchangeRateUpdate', e.target.checked)}
                    color="primary"
                  />
                }
                label="Döviz Kuru Güncellemelerinde"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnSystemErrors || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnSystemErrors', e.target.checked)}
                    color="primary"
                  />
                }
                label="Sistem Hatalarında"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnNewCustomers || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnNewCustomers', e.target.checked)}
                    color="primary"
                  />
                }
                label="Yeni Müşteri Kayıtlarında"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnLowStock || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnLowStock', e.target.checked)}
                    color="primary"
                  />
                }
                label="Düşük Stok Durumlarında"
              />
            </Grid>
            
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.notifyOnPaymentReceived || false}
                    onChange={(e) => handleNotificationSettingChange('notifyOnPaymentReceived', e.target.checked)}
                    color="primary"
                  />
                }
                label="Ödeme Alındığında"
              />
            </Grid>
            
            {notificationSettings.enableEmailNotifications && (
              <Grid sx={{ gridColumn: 'span 12', mt: 2 }}>
                <TextField
                  label="E-posta Alıcıları"
                  fullWidth
                  placeholder="ornek@firma.com, yonetici@firma.com"
                  value={notificationSettings.emailRecipients?.join(', ') || ''}
                  onChange={(e) => handleNotificationSettingChange('emailRecipients', e.target.value ? e.target.value.split(',').map(email => email.trim()) : [])}
                  helperText="Birden fazla e-posta adresini virgülle ayırın"
                />
              </Grid>
            )}
            
            {notificationSettings.enableSmsNotifications && (
              <Grid sx={{ gridColumn: 'span 12', mt: 2 }}>
                <TextField
                  label="SMS Alıcıları"
                  fullWidth
                  placeholder="+905321234567, +905551234567"
                  value={notificationSettings.smsRecipients?.join(', ') || ''}
                  onChange={(e) => handleNotificationSettingChange('smsRecipients', e.target.value ? e.target.value.split(',').map(sms => sms.trim()) : [])}
                  helperText="Birden fazla telefon numarasını virgülle ayırın"
                />
              </Grid>
            )}
            
            <Grid sx={{ gridColumn: 'span 12', mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />}
                onClick={handleSavePreferences}
                disabled={saving || settingsLoading}
              >
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* SMTP Ayarları */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          SMTP Ayarları
        </Typography>

        {smtpError && <Alert severity="error" sx={{ mb: 2 }}>{smtpError}</Alert>}
        {smtpMessage && <Alert severity="success" sx={{ mb: 2 }}>{smtpMessage}</Alert>}

        {smtpLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} display="grid">
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 8' } }}>
              <TextField
                label="Sunucu (Server)"
                fullWidth
                value={smtpSettings.Server}
                onChange={(e) => handleSmtpChange('Server', e.target.value)}
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
              <TextField
                label="Port"
                type="number"
                fullWidth
                value={smtpSettings.Port}
                onChange={(e) => handleSmtpChange('Port', Number(e.target.value))}
              />
            </Grid>

            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <TextField
                label="Kullanıcı Adı"
                fullWidth
                value={smtpSettings.Username}
                onChange={(e) => handleSmtpChange('Username', e.target.value)}
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <TextField
                label="Parola"
                type="password"
                fullWidth
                value={smtpSettings.Password}
                onChange={(e) => handleSmtpChange('Password', e.target.value)}
                placeholder={smtpSettings.Password ? '' : 'Güncellemek istemiyorsanız boş bırakın'}
              />
            </Grid>

            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <TextField
                label="Gönderen E-posta (FromEmail)"
                fullWidth
                value={smtpSettings.FromEmail}
                onChange={(e) => handleSmtpChange('FromEmail', e.target.value)}
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <TextField
                label="Gönderen Adı (FromName)"
                fullWidth
                value={smtpSettings.FromName}
                onChange={(e) => handleSmtpChange('FromName', e.target.value)}
              />
            </Grid>

            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpSettings.EnableSsl}
                    onChange={(e) => handleSmtpChange('EnableSsl', e.target.checked)}
                    color="primary"
                  />
                }
                label="SSL/TLS Kullan"
              />
            </Grid>
            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpSettings.UseDefaultCredentials}
                    onChange={(e) => handleSmtpChange('UseDefaultCredentials', e.target.checked)}
                    color="primary"
                  />
                }
                label="Varsayılan Kimlik Bilgilerini Kullan"
              />
            </Grid>

            <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
              <TextField
                label="Zaman Aşımı (ms)"
                type="number"
                fullWidth
                value={smtpSettings.Timeout}
                onChange={(e) => handleSmtpChange('Timeout', Number(e.target.value))}
              />
            </Grid>

            <Grid sx={{ gridColumn: 'span 12', mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveSmtp}
                disabled={smtpSaving || smtpLoading}
              >
                {smtpSaving ? 'Kaydediliyor...' : 'SMTP Ayarlarını Kaydet'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleTestSmtp}
                disabled={smtpTesting || smtpLoading}
              >
                {smtpTesting ? 'Test Gönderiliyor...' : 'Test E-postası Gönder'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Bildirim Tercihleri
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Grid container spacing={2} display="grid">
          {preferences.length > 0 ? (
            preferences.map((pref) => (
              <Grid key={pref.notificationType} sx={{ gridColumn: 'span 12' }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {pref.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Bildirim Türü: {pref.notificationType}
                    </Typography>
                    
                    {/* Her action için ayrı switch */}
                    {pref.actions.map((action) => (
                      <Box key={action.actionType} sx={{ mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={action.isEnabled || false} 
                              onChange={() => handleTogglePreference(pref.notificationType, action.actionType)}
                              color="primary"
                            />
                          }
                          label={`${action.displayName} (${action.isEnabled ? 'Etkin' : 'Devre dışı'})`}
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid sx={{ gridColumn: 'span 12' }}>
              <Alert severity="info">
                Henüz bildirim tercihi bulunmuyor. Sistem yöneticinizle iletişime geçin.
              </Alert>
            </Grid>
          )}
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSavePreferences}
            disabled={saving}
          >
            {saving ? 'Kaydediliyor...' : 'Tercihleri Kaydet'}
          </Button>
        </Box>
      </Paper>
      
      <Snackbar 
        open={success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess(false)}
        message="Bildirim tercihleri başarıyla kaydedildi"
      />
      <Snackbar
        open={Boolean(smtpMessage)}
        autoHideDuration={3000}
        onClose={() => setSmtpMessage(null)}
        message={smtpMessage || ''}
      />
    </Box>
  );
};

export default NotificationSettingsTab;
